import React, { CSSProperties, FC, ReactNode } from "react"
import xss from "xss"
import { renderRichText } from "gatsby-source-contentful/rich-text"

import { generateConditionalClassName } from "@utils"

import Button, { BUTTON_SIZES, BUTTON_VARIANTS } from "@components/Button/Button"

import LpSectionTitle from "./components/LpSectionTitle/LpSectionTitle"

import "./LpSection.scss"

type LpSectionProps = {
  background?: string | null
  children?: ReactNode
  className?: string
  ctaLink?: string | null
  ctaLinkSecondary?: string | null
  ctaText?: string | null
  ctaTextSecondary?: string | null
  includeBackground?: boolean | null
  marginBottom?: string | null
  subhead?: string | null
  style?: CSSProperties
  text?: { raw?: string | null } | null
  title?: string | null
  uniqueId?: string
  useDarkMode?: boolean | null
  useLeftTextAlign?: boolean | null
  useNegativeTopMargin?: boolean | null
}

const LpSection: FC<LpSectionProps> = ({
  background,
  children,
  className,
  ctaLink,
  ctaLinkSecondary,
  ctaText,
  ctaTextSecondary,
  includeBackground,
  marginBottom,
  style = {},
  subhead,
  text,
  title,
  uniqueId,
  useDarkMode,
  useLeftTextAlign,
  useNegativeTopMargin,
}) => (
  <section
    className={generateConditionalClassName({
      LpSection: true,
      "has-children": !!children,
      "has-no-text": !!title && !text,
      "include-background": !!includeBackground,
      "use-dark-mode": !!useDarkMode,
      "use-left-text-align": !!useLeftTextAlign,
      "use-negative-top-margin": !!useNegativeTopMargin,
      [`margin-bottom--${marginBottom || "XL"}`]: true,
      [`${className}`]: !!className,
      [`uid--${uniqueId}`]: !!uniqueId,
    })}
    style={{
      background: background || "",
      ...style,
    }}
  >
    {(subhead || title || text) && (
      <div className="LpSection--content container">
        <div className={`grid text-align--${useLeftTextAlign ? "left" : "center"}`}>
          {subhead && (
            <p
              className={generateConditionalClassName({
                "LpSection--subhead type--mono-S color-text--light margin-bottom--S column": true,
                "column--4-10-M column--5-9-L": !useLeftTextAlign,
                "column--1-7-M column--1-4-L": !!useLeftTextAlign,
              })}
            >
              <span dangerouslySetInnerHTML={{ __html: xss(subhead) }} />
            </p>
          )}
          {title && (
            <LpSectionTitle
              title={title}
              useLeftTextAlign={useLeftTextAlign}
            />
          )}
          {text && (
            <div className={generateConditionalClassName({
              "content column": true,
              "column--2-12-M": !useLeftTextAlign,
              "column--1-11-M": !!useLeftTextAlign,
            })}>
              <div className={useLeftTextAlign ? "" : "text-container"}>
                {typeof text?.raw === "string" &&
                  renderRichText({ raw: text.raw, references: [] })}
              </div>
            </div>
          )}
          {((ctaText && ctaLink) || (ctaLinkSecondary && ctaTextSecondary)) && (
            <div className="column button-wrapper">
              {ctaText && ctaLink && (
                <Button
                  href={ctaLink}
                  size={BUTTON_SIZES.L}
                >
                  {ctaText}
                </Button>
              )}
              {ctaTextSecondary && ctaLinkSecondary && (
                <Button
                  href={ctaLinkSecondary}
                  size={BUTTON_SIZES.L}
                  variant={BUTTON_VARIANTS.SECONDARY}
                >
                  {ctaTextSecondary}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    )}
    {children}
  </section>
)

export default LpSection
